import React, { useState, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useLocation } from '@tanstack/react-router';
import { useAuth } from '@bb/session-manager';

import { Button } from '@mui/material';

import { submitContractExportWebhook } from '../../requests/general';
import { forceSaveRequest, logDocumentExport } from '../../utils/OnlyOfficeUtils';
import OpenExportDialog from './OpenExportDialog';

const ExportButton = () => {
  const onlyOfficeUrl = useSelector(
    (state) => state.OnlyOffice.onlyOfficeUrl,
    shallowEqual
  );

  const urlSearchParams = new URLSearchParams(useLocation().searchStr || '');
  const contractId = urlSearchParams.get('contract');

  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [intervalId, setIntervalId] = useState();
  const [lockReason, setLockReason] = useState(null);

  const { config, bbAxios } = useAuth();
  const { exportWebhook = {} } = config || {};

  useEffect(() => {
    if (!contractId) {
      setLockReason(null);
      return;
    }

    bbAxios
      .get(`/api/contracts/${contractId}/export-lock`)
      .then(({ data }) => {
        setLockReason(data?.locked ? data.reason : null);
      })
      .catch(() => {
        setLockReason(null);
      });
  }, [bbAxios, contractId]);

  const handleExport = async () => {
    setOpenExportDialog(false);
    try {
      // Await the export log so lastExported is persisted before the user
      // navigates away. Without this the fire-and-forget POST could be
      // cancelled by navigation, leaving lastExported unset.
      await logDocumentExport(contractId);
    } catch (e) {
      // Even if the log call failed, still allow the download so the user
      // gets their file. The checkmark may be missing until next export.
      console.error('Export log failed, proceeding with download:', e);
    }

    if (exportWebhook.enabled !== false) {
      submitContractExportWebhook(contractId)
        .then(() => console.log('Contract export webhook submitted'))
        .catch((err) =>
          console.warn('Contract export webhook failed (non-blocking):', err.message)
        );
    }

    try {
      window.DocsAPICopy.downloadAs();
    } catch (e) {
      console.error('Download failed:', e);
    }
  };

  const handleOpenExport = async () => {
    if (lockReason) return;

    forceSaveRequest(onlyOfficeUrl, contractId)
      .then((resp) => console.log('Received response from force save request:', resp))
      .catch((err) =>
        console.log('Failed to perform force save request with error:', err)
      );

    setOpenExportDialog(true);
  };

  useEffect(() => {
    if (isDisabled) {
      const newIntervalId = setInterval(() => {
        setIsDisabled(typeof window.DocsAPI === 'undefined');
      }, 1000);
      setIntervalId(newIntervalId);
    } else {
      setIntervalId(null);
      clearInterval(intervalId);
    }
  }, [isDisabled]);

  useEffect(() => () => clearInterval(intervalId), [isDisabled]);

  return (
    <>
      <Button
        disabled={isDisabled || !!lockReason}
        color={lockReason ? 'warning' : 'primary'}
        variant="contained"
        onClick={handleOpenExport}
        title={lockReason || undefined}
      >
        {lockReason ? 'Export Locked' : 'Export Contract'}
      </Button>
      <OpenExportDialog
        open={openExportDialog}
        handleExport={handleExport}
        handleClose={() => setOpenExportDialog(false)}
      />
    </>
  );
};

export default ExportButton;
